import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { formatDate } from '@angular/common';
import { SessionsService } from 'src/app/services/sessions.service';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';

@Component({
  selector: 'app-create-session',
  templateUrl: './create-session.component.html',
  styleUrls: ['./create-session.component.scss']
})
export class CreateSessionComponent implements OnInit {

  sessionForm: FormGroup;
  eventName: string = '';
  idEvent: number;
  isEditMode: boolean = false;

  hoursOptions: string[] = [];
  typeDatepicker: string = null;

  // Opciones para repetición
  repeatUnits = [
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' }
  ];

  get repeatUnitOptions(): { id: any; label: string }[] {
    return this.repeatUnits.map(unit => ({ id: unit.value, label: unit.label }));
  }

  weekDays = [
    { value: 1, label: 'L', name: 'Lunes' },
    { value: 2, label: 'M', name: 'Martes' },
    { value: 3, label: 'X', name: 'Miércoles' },
    { value: 4, label: 'J', name: 'Jueves' },
    { value: 5, label: 'V', name: 'Viernes' },
    { value: 6, label: 'S', name: 'Sábado' },
    { value: 0, label: 'D', name: 'Domingo' }
  ];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<CreateSessionComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private sessionsService: SessionsService,
    private dialog: MatDialog
  ) {
    this.generateHoursOptions();
  }

  ngOnInit(): void {
    if (this.data) {
      this.eventName = this.data.eventName || '';
      this.idEvent = this.data.idEvent;
      this.isEditMode = this.data.isEditMode || false;
    }

    // Preparar valores iniciales para la primera sesión
    let initialSessionData = null;
    if (this.data && this.data.preselectedDate) {
      const preselectedDate = new Date(this.data.preselectedDate);

      // Siempre tomamos solo la fecha y dejamos la hora vacía para que se vea el placeholder
      initialSessionData = {
        date: preselectedDate,
        dateString: '',
        time: null,
        allDay: false
      };
    }

    // Crear el formulario con valores iniciales si hay fecha preseleccionada
    this.sessionForm = this.fb.group({
      recurring: [false],
      sessions: this.fb.array([this.createSessionGroupWithData(initialSessionData)]),
      // Campos para modo recurrente
      startDate: [null],
      startDateString: [''],
      startTime: [null],
      repeatEvery: [1],
      repeatUnit: ['week'],
      selectedDays: [[]],
      endDate: [null],
      endDateString: ['']
    });

    // Observar cambios en el toggle de recurrente
    this.sessionForm.get('recurring').valueChanges.subscribe(isRecurring => {
      if (isRecurring) {
        // Activar validaciones para modo recurrente
        this.sessionForm.get('startDate').setValidators([Validators.required]);
        this.sessionForm.get('startTime').setValidators([Validators.required]);
        this.sessionForm.get('endDate').setValidators([Validators.required]);
        this.sessionForm.get('selectedDays').setValidators([Validators.required]);
        // La lista de sesiones está oculta: sin desactivarla sus validadores
        // dejarían el formulario inválido para siempre.
        this.sessions.disable({ emitEvent: false });
      } else {
        // Quitar validaciones de modo recurrente
        this.sessionForm.get('startDate').clearValidators();
        this.sessionForm.get('startTime').clearValidators();
        this.sessionForm.get('endDate').clearValidators();
        this.sessionForm.get('selectedDays').clearValidators();
        this.sessions.enable({ emitEvent: false });
        // Al salir del modo recurrente se destruye el bloque de configuración:
        // limpiamos sus valores para que no queden fechas u horas invisibles.
        this.sessionForm.patchValue({
          startDate: null,
          startDateString: '',
          startTime: null,
          selectedDays: [],
          endDate: null,
          endDateString: ''
        }, { emitEvent: false });
      }
      this.sessionForm.get('startDate').updateValueAndValidity();
      this.sessionForm.get('startTime').updateValueAndValidity();
      this.sessionForm.get('endDate').updateValueAndValidity();
      this.sessionForm.get('selectedDays').updateValueAndValidity();
    });
  }

  createSessionGroup(): FormGroup {
    return this.fb.group({
      date: [null, Validators.required],
      dateString: [''],
      time: [null, Validators.required],
      allDay: [false]
    });
  }

  createSessionGroupWithData(data: any): FormGroup {
    if (data) {
      return this.fb.group({
        date: [data.date, Validators.required],
        dateString: [data.dateString],
        time: [data.time, Validators.required],
        allDay: [data.allDay]
      });
    }
    return this.createSessionGroup();
  }

  get sessions(): FormArray {
    return this.sessionForm.get('sessions') as FormArray;
  }

  get isRecurring(): boolean {
    return this.sessionForm.get('recurring').value;
  }

  get selectedDays(): number[] {
    return this.sessionForm.get('selectedDays').value || [];
  }

  generateHoursOptions(): void {
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour}:${minute.toString().padStart(2, '0')}`;
        this.hoursOptions.push(timeString);
      }
    }
  }

  openDatePicker(type: string, index?: number): void {
    const pickerKey = index !== undefined ? `date-${index}` : type;
    if (this.typeDatepicker === pickerKey) {
      this.typeDatepicker = null;
      return;
    }
    this.typeDatepicker = pickerKey;
  }

  saveDate(event: any, type: string, index?: number): void {
    if (event) {
      const date = event instanceof Date ? event : new Date(event);

      const hours = date.getHours();
      const minutes = date.getMinutes();
      const isDefaultTime = (hours === 0 && minutes === 0);
      const timeString = !isDefaultTime ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}` : null;

      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();

      const dateString = `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;

      const localDate = new Date(year, month, day, 0, 0, 0, 0);

      if (type === 'session' && index !== undefined) {
        const session = this.sessions.at(index);
        session.patchValue({
          date: localDate,
          dateString: dateString,
          time: timeString
        });
      } else if (type === 'startDate') {
        this.sessionForm.patchValue({
          startDate: localDate,
          startDateString: dateString,
          startTime: timeString
        });
      } else if (type === 'endDate') {
        this.sessionForm.patchValue({
          endDate: localDate,
          endDateString: dateString
        });
      }
    }
    this.typeDatepicker = null;
  }

  toggleAllDay(index: number): void {
    const session = this.sessions.at(index);
    const allDay = session.get('allDay').value;
    if (allDay) {
      session.patchValue({ time: null });
    }
  }

  toggleWeekDay(day: number): void {
    const selectedDays = [...this.selectedDays];
    const dayIndex = selectedDays.indexOf(day);

    if (dayIndex > -1) {
      selectedDays.splice(dayIndex, 1);
    } else {
      selectedDays.push(day);
    }

    this.sessionForm.patchValue({ selectedDays: selectedDays });
  }

  isWeekDaySelected(day: number): boolean {
    return this.selectedDays.includes(day);
  }

  addSession(): void {
    this.sessions.push(this.createSessionGroup());
  }

  removeSession(index: number): void {
    if (this.sessions.length > 1) {
      this.sessions.removeAt(index);
    }
  }

  getRecurringMessage(): string {
    const selectedDays = this.selectedDays;
    if (selectedDays.length === 0) return '';

    const dayNames = selectedDays
      .sort()
      .map(day => this.weekDays.find(d => d.value === day)?.name.toLowerCase())
      .join(' y ');

    return `Tiene lugar todos los ${dayNames} hasta fecha fin.`;
  }

  checkForDuplicateSessions(formValue: any): boolean {
    // Obtener sesiones existentes según el modo
    let existingSessions: any[] = [];

    if (this.isEditMode && this.data?.existingSessions) {
      // En modo edición, usar las sesiones del evento específico desde el servidor
      existingSessions = this.data.existingSessions;
    } else {
      // En modo creación, usar las sesiones temporales del observable
      existingSessions = this.sessionsService.getSessionsSaved();
    }

    // Construir lista de nuevas sesiones con sus timestamps
    const newSessions: { date: string; time: string }[] = [];

    if (formValue.recurring) {
      // Para sesiones recurrentes, generar todas las fechas
      const generatedSessions = this.generateRecurringSessions(
        formValue.startDate,
        formValue.endDate,
        formValue.selectedDays,
        formValue.repeatEvery,
        formValue.repeatUnit,
        formValue.startTime
      );

      generatedSessions.forEach(session => {
        const startDate = new Date(session.start_date);
        const dateStr = formatDate(startDate, 'yyyy-MM-dd', 'en-US');
        const timeStr = formatDate(startDate, 'HH:mm', 'en-US');
        newSessions.push({ date: dateStr, time: timeStr });
      });
    } else {
      // Para sesiones normales
      formValue.sessions.forEach((session: any) => {
        if (session.date && session.time) {
          const dateStr = formatDate(session.date, 'yyyy-MM-dd', 'en-US');
          const timeStr = session.time;
          newSessions.push({ date: dateStr, time: timeStr });
        }
      });
    }
    // Verificar contra sesiones existentes
    for (const newSession of newSessions) {
      const duplicate = existingSessions.find(existing => {
        let existingDateStr: string;
        let existingTimeStr: string;

        // Sesiones del backend llevan Z (UTC real almacenado como hora local).
        // Sesiones temporales no llevan Z y son strings locales directos.
        if (typeof existing.start_date === 'string' && existing.start_date.includes('Z')) {
          const existingDate = new Date(existing.start_date);
          existingDateStr = formatDate(existingDate, 'yyyy-MM-dd', 'en-US', '+0000');
          existingTimeStr = formatDate(existingDate, 'HH:mm', 'en-US', '+0000');
        } else {
          const parts = (existing.start_date as string).replace('T', ' ').split(' ');
          existingDateStr = parts[0];
          existingTimeStr = parts[1] ? parts[1].substring(0, 5) : '00:00';
        }

        return existingDateStr === newSession.date && existingTimeStr === newSession.time;
      });

      if (duplicate) {
        const [year, month, day] = newSession.date.split('-');
        const dateFormatted = `${day}/${month}/${year}`;

        this.dialog.open(InfoCardComponent, {
          panelClass: ['info-dialog'],
          disableClose: false,
          data: {
            icon: true,
            text: `Ya existe una sesión el ${dateFormatted} a las ${newSession.time}h. No se pueden crear sesiones duplicadas en el mismo horario.`,
            warning: true,
            btnCancel: false
          }
        });
        return true;
      }
    }

    return false;
  }

  saveSession(): void {
    this.sessionForm.markAllAsTouched();

    if (this.sessionForm.invalid) {
      return;
    }

    const formValue = this.sessionForm.value;
    let sessionsToSave = [];

    // Validar sesiones duplicadas antes de guardar
    const hasDuplicates = this.checkForDuplicateSessions(formValue);
    if (hasDuplicates) {
      return;
    }

    if (formValue.recurring) {
      // Generar sesiones recurrentes
      sessionsToSave = this.generateRecurringSessions(
        formValue.startDate,
        formValue.endDate,
        formValue.selectedDays,
        formValue.repeatEvery,
        formValue.repeatUnit,
        formValue.startTime
      );

      if (sessionsToSave.length === 0) {
        return;
      }

      // Construir configuración de recurrencia
      const startDate = new Date(formValue.startDate);
      const startTime = formValue.startTime || '00:00';
      const [hours, minutes] = startTime.split(':').map(Number);
      startDate.setHours(hours, minutes, 0, 0);

      const endDate = new Date(formValue.endDate);
      endDate.setHours(23, 59, 59, 999);

      const recurringSession = {
        start_date: formatDate(startDate, 'yyyy-MM-dd HH:mm:ss', 'en-US'),
        end_date: formatDate(endDate, 'yyyy-MM-dd HH:mm:ss', 'en-US'),
        weekdays: formValue.selectedDays,
        repeat_every: formValue.repeatEvery,
        repeat_unit: formValue.repeatUnit,
        all_day: false
      };

      const payload = {
        recurringSession: recurringSession,
        sessions: sessionsToSave
      };

      if (this.isEditMode && this.idEvent) {
        this.sessionsService.createRecurringSessions(payload, this.idEvent).subscribe({
          next: (response) => {
            this.dialogRef.close({ success: true, sessions: response.data, recurring: true });
          },
          error: (error) => {
            console.error('Error creating recurring sessions:', error);
            this.dialogRef.close({ success: false, error });
          }
        });
      } else {
        // Modo creación: guardar temporalmente
        const sessionsWithTemp = sessionsToSave.map(s => ({ ...s, _isTemporary: true }));
        const currentSessions = this.sessionsService.getSessionsSaved();
        this.sessionsService.setSessionsSaved([...currentSessions, ...sessionsWithTemp]);
        this.dialogRef.close({
          success: true,
          sessions: sessionsWithTemp,
          recurring: true,
          recurringSession: recurringSession
        });
      }
    } else {
      // Sesiones normales
      sessionsToSave = formValue.sessions.map(session => this.buildSessionPayload(session));

      if (this.isEditMode && this.idEvent) {
        this.sessionsService.createSessionsEvent(sessionsToSave, this.idEvent).subscribe({
          next: (response) => {
            this.dialogRef.close({ success: true, sessions: response.data });
          },
          error: (error) => {
            console.error('Error creating sessions:', error);
            this.dialogRef.close({ success: false, error });
          }
        });
      } else {
        const currentSessions = this.sessionsService.getSessionsSaved();
        this.sessionsService.setSessionsSaved([...currentSessions, ...sessionsToSave]);
        this.dialogRef.close({ success: true, sessions: sessionsToSave });
      }
    }
  }

  private generateRecurringSessions(startDate: Date, endDate: Date, selectedDays: number[], repeatEvery: number, repeatUnit: string, time: string): any[] {
    const sessions = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);

    while (currentDate <= end) {
      if (selectedDays.includes(currentDate.getDay())) {
        sessions.push(this.buildSessionPayload({
          date: new Date(currentDate),
          time: time || '00:00',
          allDay: false
        }));
      }

      if (repeatUnit === 'day') {
        currentDate.setDate(currentDate.getDate() + repeatEvery);
      } else if (repeatUnit === 'week') {
        currentDate.setDate(currentDate.getDate() + 1);
      } else if (repeatUnit === 'month') {
        currentDate.setMonth(currentDate.getMonth() + repeatEvery);
      }
    }

    return sessions;
  }

  private buildSessionPayload(session: any): any {
    const date = new Date(session.date);
    const [hours, minutes] = session.time.split(':').map(Number);

    const sessionDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      0,
      0
    );

    const formattedDate = formatDate(sessionDate, 'yyyy-MM-dd HH:mm:ss', 'en-US');

    const payload: any = {
      id_event: this.idEvent,
      id_recurring_session: null,
      start_date: formattedDate
    };

    if (!this.isEditMode && !this.idEvent) {
      payload._isTemporary = true;
    }

    return payload;
  }

  close(): void {
    this.dialogRef.close();
  }
}
